import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { List, Bell, Clock, MapPin, CheckCircle, SignOut, SignIn, ChalkboardTeacher, CaretLeft, CaretRight, Images, BookOpen, Table, WarningCircle, X } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { wibNow } from "@/lib/time";
import { useAuth } from "@/context/AuthContext";
import { GalleryCarousel } from "@/components/GalleryCarousel";
import { LearningMediaCarousel } from "@/components/LearningMediaCarousel";
import { WeeklyHoursCard } from "@/components/WeeklyHoursCard";
import { toast } from "sonner";

const DAYS = ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jum'at", "Sabtu"];
const MON = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];

export default function TeacherHome() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isSdqu = user?.school === "sdqu";
  const [now, setNow] = useState(wibNow());
  const [sum, setSum] = useState(null);
  const [busy, setBusy] = useState(false);
  const [piket, setPiket] = useState(null);
  const [piketBusy, setPiketBusy] = useState(false);
  const [soreBusy, setSoreBusy] = useState(false);
  const [gallery, setGallery] = useState([]);
  const [videos, setVideos] = useState([]);
  const [accum, setAccum] = useState(null);
  const [adminMsgs, setAdminMsgs] = useState([]);
  const [msgReply, setMsgReply] = useState({});
  const [accMonth, setAccMonth] = useState(wibNow().getMonth() + 1);
  const [accYear, setAccYear] = useState(wibNow().getFullYear());
  const [fullscreenImg, setFullscreenImg] = useState(null);
  const [notif, setNotif] = useState({ unread: 0, items: [] });
  const [notifOpen, setNotifOpen] = useState(false);
  const loadNotif = () => api.get("/teacher/notifications").then((r) => setNotif(r.data)).catch(() => {});
  useEffect(() => { loadNotif(); const t = setInterval(loadNotif, 30000); return () => clearInterval(t); }, []);
  const openNotif = async () => { setNotifOpen(true); try { await api.post("/teacher/notifications/read"); } catch (e) {} loadNotif(); };
  useEffect(() => { if (new URLSearchParams(location.search).get("notif") === "1") openNotif(); }, [location.search]); // eslint-disable-line

  useEffect(() => { const t = setInterval(() => setNow(wibNow()), 1000); return () => clearInterval(t); }, []);
  const load = () => api.get("/teacher/attendance/summary").then((r) => setSum(r.data)).catch(() => {});
  const loadPiket = () => api.get("/teacher/piket/today").then((r) => setPiket(r.data)).catch(() => {});
  useEffect(() => { load(); loadPiket(); api.get("/teacher/messages").then((r) => setAdminMsgs(r.data)).catch(() => {}); }, []);
  const respondMsg = async (id) => {
    try { await api.post(`/teacher/messages/${id}/respond`, { reply: msgReply[id] || "" }); setAdminMsgs((a) => a.filter((m) => m.id !== id)); toast.success("Terima kasih, InsyaAllah"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  useEffect(() => { api.get(`/teacher/accumulation/mine?month=${accMonth}&year=${accYear}`).then((r) => setAccum(r.data)).catch(() => {}); }, [accMonth, accYear]);
  const accCur = wibNow();
  const accIsCurrent = accYear > accCur.getFullYear() || (accYear === accCur.getFullYear() && accMonth >= accCur.getMonth() + 1);
  const accPrev = () => { if (accMonth === 1) { setAccMonth(12); setAccYear(accYear - 1); } else setAccMonth(accMonth - 1); };
  const accNext = () => { if (accIsCurrent) return; if (accMonth === 12) { setAccMonth(1); setAccYear(accYear + 1); } else setAccMonth(accMonth + 1); };
  useEffect(() => {
    api.get("/gallery").then((r) => setGallery(r.data)).catch(() => {});
    api.get("/gallery/videos").then((r) => setVideos(r.data)).catch(() => {});
  }, []);

  const today = sum?.today;
  const clock = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;

  const tGroup = (k) => (!k ? "" : k.startsWith("Kelas ") ? "Kelas " + k.split(" ")[1][0] : k.startsWith("TK B") ? "TK B" : k.startsWith("TK A") ? "TK A" : k.startsWith("KB") ? "KB" : "");
  const myKelas = user?.kelas || "";
  // Tiered fallback agar galeri depan tidak kosong: sub-kelas → kelas(grup) → Semua
  const pickTiered = (items) => {
    const grp = tGroup(myKelas);
    const t1 = items.filter((g) => g.kelas === myKelas);
    if (t1.length) return t1;
    const t2 = grp ? items.filter((g) => g.kelas === grp) : [];
    if (t2.length) return t2;
    return items.filter((g) => g.kelas === "Semua");
  };
  const galleryPhotos = pickTiered(gallery);
  const galleryVideos = pickTiered(videos);

  const doCheckin = async () => {
    setBusy(true);
    try { await api.post("/teacher/attendance/checkin"); toast.success("Absen masuk tercatat"); await load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const doCheckout = async () => {
    setBusy(true);
    try { await api.post("/teacher/attendance/checkout"); toast.success("Absen pulang tercatat"); await load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const doPiket = async () => {
    setPiketBusy(true);
    try { const { data } = await api.post("/teacher/piket"); toast.success(data.status === "tepat_waktu" ? "Piket tepat waktu tercatat" : "Piket terlambat tercatat"); await loadPiket(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setPiketBusy(false); }
  };
  const doPiketSore = async () => {
    setSoreBusy(true);
    try { await api.post("/teacher/piket/sore"); toast.success("Piket sore tercatat"); await loadPiket(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSoreBusy(false); }
  };
  const piketMins = now.getHours() * 60 + now.getMinutes();
  const piketWindow = piketMins <= 420 ? "tepat_waktu" : piketMins <= 430 ? "terlambat" : null;
  const soreWindow = piketMins >= 17 * 60 + 30 && piketMins <= 19 * 60;

  const checkedIn = !!today?.time_in;
  const importantMsgs = adminMsgs.filter((m) => m.important);
  const normalMsgs = adminMsgs.filter((m) => !m.important);
  const checkedOut = !!today?.time_out;
  const terlambat = today?.status === "Terlambat";

  return (
    <div className="pb-4" data-testid="teacher-dashboard">
      {notifOpen && (
        <div className="fixed inset-0 z-[70] bg-black/40 flex items-end md:items-center justify-center" onClick={() => setNotifOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full md:max-w-md rounded-t-3xl md:rounded-3xl p-5 max-h-[80vh] overflow-y-auto" data-testid="teacher-notif-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg text-stone-800">Notifikasi</h3>
              <button onClick={() => setNotifOpen(false)}><X size={22} className="text-stone-400" /></button>
            </div>
            {notif.items.length === 0 ? (
              <p className="text-center text-stone-400 py-8 text-sm">Belum ada notifikasi.</p>
            ) : (
              <div className="space-y-2">
                {notif.items.map((n, i) => (
                  <button key={i} data-testid={`teacher-notif-item-${i}`} onClick={() => { setNotifOpen(false); navigate(`/siswa/${n.student_id}?tab=${n.category === "evaluasi_harian" ? "hafalan" : "laporan"}`); }}
                    className={`w-full text-left rounded-2xl p-3 border active:scale-[0.99] transition-transform ${n.read ? "bg-white border-stone-200/60" : "bg-primary/5 border-primary/30"}`}>
                    <p className="text-xs text-stone-400 mb-0.5">{n.category === "kegiatan_harian" ? "Catatan Guru" : n.category === "evaluasi_harian" ? "Hafalan" : n.item_label} · {n.student_name}</p>
                    <p className="text-sm text-stone-700"><b className="text-primary">{n.by_name || "Orang Tua"}:</b> {n.reaction ? "👍 " : ""}{n.text || "(reaksi)"}</p>
                    <p className="text-[11px] text-stone-400 mt-1">{new Date(n.at).toLocaleString("id-ID", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })} · <span className="text-primary font-semibold">Lihat detail →</span></p>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {importantMsgs.length > 0 && (
        <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-4" data-testid="important-msg-modal">
          <div className="bg-white w-full max-w-md rounded-3xl p-5 max-h-[85vh] overflow-y-auto border-4 border-red-400">
            <p className="text-[11px] font-bold text-red-600 uppercase tracking-wide mb-2 flex items-center gap-1"><WarningCircle size={15} weight="fill" /> Pesan Penting dari Admin</p>
            <p className="font-bold text-stone-800 text-lg" data-testid={`important-msg-${importantMsgs[0].id}`}>{importantMsgs[0].title}</p>
            <p className="text-sm text-stone-700 whitespace-pre-wrap mt-2">{importantMsgs[0].body}</p>
            <textarea data-testid={`important-reply-${importantMsgs[0].id}`} value={msgReply[importantMsgs[0].id] || ""} onChange={(e) => setMsgReply((p) => ({ ...p, [importantMsgs[0].id]: e.target.value }))}
              rows={2} placeholder="Tulis jawaban (opsional)..." className="w-full mt-4 px-3 py-2 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-red-400 resize-none" />
            <button data-testid={`important-insyaallah-${importantMsgs[0].id}`} onClick={() => respondMsg(importantMsgs[0].id)}
              className="w-full mt-2 bg-green-600 text-white font-bold py-3 rounded-full text-sm flex items-center justify-center gap-2 active:scale-95 transition-transform">
              <CheckCircle size={18} weight="bold" /> InsyaAllah
            </button>
            {importantMsgs.length > 1 && <p className="text-center text-[11px] text-stone-400 mt-2">{importantMsgs.length - 1} pesan penting lagi</p>}
          </div>
        </div>
      )}
      {/* Header */}
      <div className="bg-primary text-white px-5 pt-6 pb-8 rounded-b-[2rem] relative overflow-hidden">
        <div className="absolute -right-10 -top-10 w-40 h-40 bg-white/10 rounded-full" />
        <div className="flex items-center justify-between relative">
          <button data-testid="header-menu-btn" onClick={() => navigate("/kelola")} className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center active:scale-90 transition-transform">
            <List size={20} weight="bold" />
          </button>
          <h1 className="font-heading font-bold text-lg">Dashboard Guru</h1>
          <button data-testid="header-bell-btn" onClick={openNotif} className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center relative active:scale-90 transition-transform">
            <Bell size={20} weight="fill" />
            {notif.unread > 0 && <span data-testid="teacher-notif-badge" className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-red-500 text-[10px] font-bold rounded-full flex items-center justify-center">{notif.unread}</span>}
          </button>
        </div>
        <div className="flex items-end justify-between mt-4 relative gap-2">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-white/95 flex items-center justify-center shrink-0 overflow-hidden p-0.5">
              <img src={isSdqu ? "/sdqu_logo.png" : "/tk_logo.png"} alt="Muslim Kids" className="w-full h-full object-contain" />
            </div>
            <div className="min-w-0">
              <p className="font-heading text-sm font-bold leading-tight">Assalamu'alaikum 👋</p>
              <p data-testid="teacher-name" className="text-white text-sm font-bold leading-tight truncate">Umi Guru {user?.name}</p>
              <p className="text-white/80 text-xs mt-0.5">{DAYS[now.getDay()]}, {now.getDate()} {MON[now.getMonth()]} {now.getFullYear()}</p>
            </div>
          </div>
          <div data-testid="live-clock" className="bg-white/20 rounded-full px-3 py-1.5 text-sm font-bold flex items-center gap-1 shrink-0">
            <Clock size={15} weight="fill" /> {clock} WIB
          </div>
        </div>
      </div>

      {/* Admin messages + Attendance status card */}
      <div className="px-5 -mt-5 relative space-y-3">
        {normalMsgs.map((m) => (
          <div key={m.id} data-testid={`admin-msg-${m.id}`} className="bg-amber-50 border-2 border-amber-300 rounded-3xl shadow-lg p-4">
            <p className="text-[11px] font-bold text-amber-600 uppercase tracking-wide mb-1 flex items-center gap-1"><Bell size={13} weight="fill" /> Pesan dari Admin</p>
            <p className="font-bold text-stone-800 text-base">{m.title}</p>
            <p className="text-sm text-stone-700 whitespace-pre-wrap mt-1">{m.body}</p>
            <textarea data-testid={`admin-msg-reply-${m.id}`} value={msgReply[m.id] || ""} onChange={(e) => setMsgReply((p) => ({ ...p, [m.id]: e.target.value }))}
              rows={2} placeholder="Tulis jawaban (opsional)..." className="w-full mt-3 px-3 py-2 rounded-xl border border-amber-200 text-sm bg-white focus:outline-none focus:border-amber-400 resize-none" />
            <button data-testid={`admin-msg-insyaallah-${m.id}`} onClick={() => respondMsg(m.id)}
              className="w-full mt-2 bg-green-600 text-white font-bold py-2.5 rounded-full text-sm flex items-center justify-center gap-2 active:scale-95 transition-transform">
              <CheckCircle size={18} weight="bold" /> InsyaAllah
            </button>
          </div>
        ))}
        <div className="bg-white rounded-3xl border border-stone-200/60 shadow-lg p-5">
          <h3 className="font-heading font-semibold text-stone-800 mb-3">Status Kehadiran Hari Ini</h3>

          {!checkedIn ? (
            <button data-testid="absen-masuk-btn" disabled={busy} onClick={doCheckin}
              className="w-full py-5 rounded-2xl bg-green-600 text-white font-heading font-bold text-lg shadow-md flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-60">
              <SignIn size={26} weight="bold" /> ABSEN MASUK
            </button>
          ) : (
            <>
              <div className="bg-green-50 border border-green-200 rounded-2xl p-4 text-center">
                <p className="text-green-700 font-semibold text-sm flex items-center justify-center gap-1.5">
                  <CheckCircle size={18} weight="fill" /> SUDAH ABSEN MASUK
                </p>
                <p data-testid="time-in-value" className="font-heading text-4xl font-bold text-green-700 mt-1">{today.time_in.replace(".", ":")} <span className="text-lg">WIB</span></p>
                <span className={`inline-block mt-2 text-xs font-bold px-3 py-1 rounded-full ${terlambat ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>
                  {terlambat ? "Terlambat" : "Hadir"}
                </span>
              </div>
              <div className="flex items-center justify-between mt-3 text-sm">
                <span className="flex items-center gap-1.5 text-stone-600"><MapPin size={16} weight="fill" className="text-green-600" /> Dalam area sekolah</span>
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">Terverifikasi</span>
              </div>

              {/* Piket pagi & pulang */}
              <div className="mt-3 border-t border-stone-100 pt-3">
                {piket?.piket ? (
                  <div data-testid="piket-done" className={`w-full py-3 rounded-2xl font-heading font-bold text-base flex items-center justify-center gap-2 ${piket.piket === "tepat_waktu" ? "bg-orange-100 text-orange-700" : "bg-amber-100 text-amber-700"}`}>
                    <CheckCircle size={20} weight="fill" /> Piket {piket.piket === "tepat_waktu" ? "Tepat Waktu" : "Terlambat"} ✓
                  </div>
                ) : (
                  <button data-testid="piket-btn" disabled={piketBusy || !piketWindow} onClick={doPiket}
                    className={`w-full py-4 rounded-2xl font-heading font-bold text-base shadow-md flex items-center justify-center gap-2 transition-transform ${piketWindow ? "bg-orange-500 text-white active:scale-95" : "bg-stone-200 text-stone-400 cursor-not-allowed"}`}>
                    <Clock size={22} weight="bold" /> Piket Pagi
                  </button>
                )}
                <div className="mt-2 text-[11px] text-stone-500 leading-relaxed" data-testid="piket-keterangan">
                  <p className="font-semibold text-stone-600 mb-0.5">Ketentuan piket:</p>
                  <p>• Sebelum 07:00 — <span className="text-orange-600 font-semibold">tepat waktu</span></p>
                  <p>• 07:01 – 07:10 — <span className="text-amber-600 font-semibold">terlambat</span></p>
                  <p>• Di atas 07:10 — tidak dihitung piket</p>
                </div>
              </div>
              <div className="flex items-center justify-between mt-2 text-sm border-t border-stone-100 pt-2">
                <span className="text-stone-500">Jam pulang</span>
                <span data-testid="time-out-value" className={`font-semibold ${checkedOut ? "text-stone-800" : "text-stone-400"}`}>
                  {checkedOut ? `${today.time_out.replace(".", ":")} WIB` : "Belum melakukan absensi"}
                </span>
              </div>
              {!checkedOut && (
                <button data-testid="absen-pulang-btn" disabled={busy} onClick={doCheckout}
                  className="w-full mt-3 py-4 rounded-2xl bg-green-600 text-white font-heading font-bold text-base shadow-md flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-60">
                  <SignOut size={22} weight="bold" /> ABSEN PULANG
                </button>
              )}

              {/* Piket sore */}
              <div className="mt-3 border-t border-stone-100 pt-3">
                {piket?.piket_sore ? (
                  <div data-testid="piket-sore-done" className="w-full py-3 rounded-2xl font-heading font-bold text-base flex items-center justify-center gap-2 bg-orange-100 text-orange-700">
                    <CheckCircle size={20} weight="fill" /> Piket Sore ✓
                  </div>
                ) : (
                  <button data-testid="piket-sore-btn" disabled={soreBusy || !soreWindow} onClick={doPiketSore}
                    className={`w-full py-4 rounded-2xl font-heading font-bold text-base shadow-md flex items-center justify-center gap-2 transition-transform ${soreWindow ? "bg-orange-500 text-white active:scale-95" : "bg-stone-200 text-stone-400 cursor-not-allowed"}`}>
                    <Clock size={22} weight="bold" /> Piket Sore
                  </button>
                )}
                <div className="mt-2 text-[11px] text-stone-500 leading-relaxed" data-testid="piket-sore-keterangan">
                  <p>Pulang jam 18:00 dan atau murid telah selesai dijemput</p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Jam Masuk per Hari */}
      <div className="px-5 mt-5">
        <WeeklyHoursCard />
      </div>

      {/* Akumulasi Kehadiran Bulanan (guru ini saja) */}
      {accum && (
        <div className="px-5 mt-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-heading font-bold text-stone-800 text-sm">Akumulasi Kehadiran Guru Bulanan</h3>
              <p className="text-xs text-stone-500">{MON[accMonth - 1]} {accYear}</p>
            </div>
            <div className="flex gap-2">
              <button data-testid="akum-prev-month" onClick={accPrev} className="w-9 h-9 rounded-full bg-stone-100 flex items-center justify-center active:scale-95 transition-transform"><CaretLeft size={16} weight="bold" /></button>
              <button data-testid="akum-next-month" onClick={accNext} disabled={accIsCurrent} className={`w-9 h-9 rounded-full flex items-center justify-center transition-transform ${accIsCurrent ? "bg-stone-50 text-stone-300" : "bg-stone-100 active:scale-95"}`}><CaretRight size={16} weight="bold" /></button>
            </div>
          </div>
          <div className="bg-white rounded-3xl border border-stone-200/60 shadow-sm p-3 overflow-x-auto no-scrollbar" data-testid="guru-akumulasi-card">
            <table className="w-full border-collapse text-xs" data-testid="guru-akumulasi-table">
              <thead>
                <tr className="bg-teal-800 text-white">
                  <th className="px-2 py-2 text-left font-bold min-w-[110px]">Nama Guru</th>
                  <th className="px-1.5 py-2 text-center font-bold">Kehadiran</th>
                  <th className="px-1.5 py-2 text-center font-bold">Izin</th>
                  <th className="px-1.5 py-2 text-center font-bold">Sakit</th>
                  <th className="px-1.5 py-2 text-center font-bold">Piket Pagi</th>
                  <th className="px-1.5 py-2 text-center font-bold">Piket Sore</th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-teal-50/40">
                  <td className="px-2 py-2 border-t border-teal-100"><p className="font-semibold text-stone-800 leading-tight">{accum.name}</p><p className="text-[10px] text-stone-400">{accum.kelas || "—"}</p></td>
                  <td className="px-1.5 py-2 text-center font-bold text-green-700 border-t border-teal-100">{accum.kehadiran > 0 ? accum.kehadiran : "–"}</td>
                  <td className="px-1.5 py-2 text-center text-blue-600 border-t border-teal-100">{accum.izin > 0 ? accum.izin : "–"}</td>
                  <td className="px-1.5 py-2 text-center text-amber-600 border-t border-teal-100">{accum.sakit > 0 ? accum.sakit : "–"}</td>
                  <td className="px-1.5 py-2 text-center text-orange-600 border-t border-teal-100">{accum.piket_pagi > 0 ? accum.piket_pagi : "–"}</td>
                  <td className="px-1.5 py-2 text-center text-orange-600 border-t border-teal-100">{accum.piket_sore > 0 ? accum.piket_sore : "–"}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Kegiatan Guru */}
      <div className="px-5 mt-5">
        <button data-testid="kegiatan-guru-btn" onClick={() => navigate("/kegiatan")}
          className="w-full bg-primary text-white rounded-3xl p-5 flex items-center gap-4 shadow-lg active:scale-[0.98] transition-transform relative overflow-hidden">
          <div className="absolute -right-6 -bottom-6 w-24 h-24 bg-white/10 rounded-full" />
          <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center shrink-0">
            <ChalkboardTeacher size={30} weight="fill" />
          </div>
          <div className="flex-1 text-left relative">
            <p className="font-heading font-bold text-lg">Kegiatan Guru</p>
            <p className="text-white/80 text-xs">Data siswa per kelas, hafalan & laporan</p>
          </div>
          <CaretRight size={22} weight="bold" className="relative" />
        </button>
      </div>

      {/* Kehadiran quick actions */}
      <div className="px-5 mt-4 grid grid-cols-2 gap-3">
        <button data-testid="teacher-detail-kehadiran-btn" onClick={() => navigate("/riwayat?detail=1")}
          className="bg-white rounded-2xl border border-stone-200/60 p-4 flex items-center gap-3 shadow-sm active:scale-95 transition-transform">
          <div className="w-11 h-11 rounded-2xl bg-teal-100 text-teal-600 flex items-center justify-center"><Table size={22} weight="duotone" /></div>
          <span className="font-heading font-semibold text-stone-700 text-sm text-left leading-tight">Detail Kehadiran</span>
        </button>
        <button data-testid="teacher-ajukan-izin-btn" onClick={() => navigate("/riwayat?izin=1")}
          className="bg-white rounded-2xl border border-stone-200/60 p-4 flex items-center gap-3 shadow-sm active:scale-95 transition-transform">
          <div className="w-11 h-11 rounded-2xl bg-amber-100 text-amber-600 flex items-center justify-center"><WarningCircle size={22} weight="duotone" /></div>
          <span className="font-heading font-semibold text-stone-700 text-sm text-left leading-tight">Ajukan Izin</span>
        </button>
      </div>

      {/* Quick links: Galeri & Materi */}
      <div className="px-5 mt-4 grid grid-cols-2 gap-3">
        <button data-testid="teacher-galeri-btn" onClick={() => navigate("/galeri")}
          className="bg-white rounded-2xl border border-stone-200/60 p-4 flex items-center gap-3 shadow-sm active:scale-95 transition-transform">
          <div className="w-11 h-11 rounded-2xl bg-purple-100 text-purple-600 flex items-center justify-center"><Images size={22} weight="duotone" /></div>
          <span className="font-heading font-semibold text-stone-700 text-sm text-left">Galeri</span>
        </button>
        <button data-testid="teacher-materi-btn" onClick={() => navigate("/materi")}
          className="bg-white rounded-2xl border border-stone-200/60 p-4 flex items-center gap-3 shadow-sm active:scale-95 transition-transform">
          <div className="w-11 h-11 rounded-2xl bg-amber-100 text-amber-600 flex items-center justify-center"><BookOpen size={22} weight="duotone" /></div>
          <span className="font-heading font-semibold text-stone-700 text-sm text-left">Materi</span>
        </button>
      </div>

      {/* Video Pembelajaran (di atas Galeri) */}
      <LearningMediaCarousel />

      {/* Galeri kelas (carousel) */}
      <div className="px-5 mt-6">
        <div className="flex items-center justify-between mb-2.5">
          <h3 className="font-heading font-semibold text-stone-800 flex items-center gap-2">
            <Images size={24} weight="fill" className="text-purple-500" /> Galeri {myKelas || "Kelas"}
          </h3>
          <button onClick={() => navigate("/galeri")} data-testid="teacher-lihat-semua-galeri"
            className="bg-primary text-white text-xs font-semibold px-3 py-1.5 rounded-full active:scale-95 transition-transform">Lihat Semua Galeri</button>
        </div>
        <GalleryCarousel videos={galleryVideos} photos={galleryPhotos.slice(0, 8)} onFullscreen={setFullscreenImg} />
      </div>

      {fullscreenImg && (
        <div data-testid="gallery-fullscreen" onClick={() => setFullscreenImg(null)}
          className="fixed inset-0 z-[80] bg-black/90 flex items-center justify-center p-4 animate-in fade-in">
          <button data-testid="gallery-fullscreen-close" onClick={() => setFullscreenImg(null)}
            className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/15 text-white flex items-center justify-center active:scale-90 transition-transform">
            <X size={24} weight="bold" />
          </button>
          <img src={fullscreenImg} alt="galeri" loading="lazy" className="max-w-full max-h-full object-contain rounded-2xl" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}

function SumCell({ v, l, c, bg }) {
  return (
    <div className={`${bg} rounded-2xl p-2.5 text-center`}>
      <p className={`font-heading font-bold text-2xl ${c}`}>{v ?? 0}</p>
      <p className="text-[9px] text-stone-500 font-medium leading-tight mt-0.5">{l}</p>
    </div>
  );
}
